import { useEffect, useState, useCallback } from 'react';
import { useLazyGetAffiliateDetailsQuery } from '../apis/affiliate';
import { BRANCH_CODE } from '../constants';
import { useAppDispatch } from '../reducers';
import { updateAgent } from '../reducers/application';
import { Agent } from '../types/model';
import useQueryParam from './useQueryParam';
import { useAgentCode } from '@/utils/agent';

/**
 * Affiliate state returned by the useAffiliate hook.
 */
export type AffiliateState = {
  /** Whether an affiliate ID is present in the URL */
  isAffiliate: boolean;
  /** The affiliate GUID from URL query param */
  affiliateGuid: string;
  /** Agent code (from affiliate API or default) */
  agentCode: string;
  /** Agent branch code (from affiliate API or default) */
  agentBranch: string;
  /** Whether affiliate data is currently loading */
  isLoading: boolean;
  /** Error message if affiliate fetch failed */
  error: string | null;
};

/**
 * Custom hook to handle affiliate link behavior.
 *
 * This hook:
 * 1. Reads the `aid` query parameter from the URL
 * 2. If present, fetches affiliate details from the API
 * 3. Updates redux state with agent code and branch
 * 4. Falls back to default agent code if API fails or no affiliate ID
 *
 * @returns AffiliateState containing affiliate information and loading state
 *
 * @example
 * const { isAffiliate, agentCode, agentBranch, isLoading } = useAffiliate();
 *
 * // Render affiliate info when applicable
 * {isAffiliate && <div>Agent: {agentCode} - {agentBranch}</div>}
 */
export const useAffiliate = (): AffiliateState => {
  const [affiliateGuid] = useQueryParam('aid', '');
  const dispatch = useAppDispatch();
  const defaultAgentCode = useAgentCode();

  const [agentCode, setAgentCode] = useState<string>(defaultAgentCode);
  const [agentBranch, setAgentBranch] = useState<string>(BRANCH_CODE);
  const [error, setError] = useState<string | null>(null);

  const [getAffiliateDetails, { isLoading, isFetching }] =
    useLazyGetAffiliateDetailsQuery();

  const isAffiliate = Boolean(affiliateGuid);

  // Fetch affiliate details and update redux when affiliate ID is present
  const fetchAffiliateDetails = useCallback(async () => {
    if (!affiliateGuid) {
      // No affiliate ID - use default agent code
      setAgentCode(defaultAgentCode);
      setAgentBranch(BRANCH_CODE);
      dispatch(
        updateAgent({
          agent: {
            agentCode: defaultAgentCode,
            agentBranch: BRANCH_CODE,
          } as Agent,
        }),
      );
      return;
    }

    try {
      setError(null);
      const result = await getAffiliateDetails(affiliateGuid).unwrap();

      if (result?.data) {
        const { agentCode: fetchedAgentCode, agentBranchCode } = result.data;

        // Validate we have the required data
        if (fetchedAgentCode && agentBranchCode) {
          setAgentCode(fetchedAgentCode);
          setAgentBranch(agentBranchCode);

          dispatch(
            updateAgent({
              agent: {
                agentCode: fetchedAgentCode,
                agentBranch: agentBranchCode,
                agentName: result.data.agentName,
              } as Agent,
            }),
          );
        } else {
          // Incomplete data - fall back to defaults
          console.warn(
            'Affiliate API returned incomplete data, using defaults',
          );
          setAgentCode(defaultAgentCode);
          setAgentBranch(BRANCH_CODE);
          dispatch(
            updateAgent({
              agent: {
                agentCode: defaultAgentCode,
                agentBranch: BRANCH_CODE,
              } as Agent,
            }),
          );
        }
      } else {
        // No data returned - fall back to defaults
        throw new Error('No affiliate data returned');
      }
    } catch (err) {
      console.error('Failed to fetch affiliate details:', err);
      setError('Failed to load affiliate information');

      // Fall back to default agent code on error
      setAgentCode(defaultAgentCode);
      setAgentBranch(BRANCH_CODE);
      dispatch(
        updateAgent({
          agent: {
            agentCode: defaultAgentCode,
            agentBranch: BRANCH_CODE,
          } as Agent,
        }),
      );
    }
  }, [affiliateGuid, defaultAgentCode, dispatch, getAffiliateDetails]);

  // Trigger affiliate fetch on mount or when affiliateGuid changes
  useEffect(() => {
    fetchAffiliateDetails();
  }, [fetchAffiliateDetails]);

  return {
    isAffiliate,
    affiliateGuid,
    agentCode,
    agentBranch,
    isLoading: isLoading || isFetching,
    error,
  };
};
